// frontend/src/pages/Ingestion.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getOrganizations } from "../api/accessProvisioning";
import {
  wipeReingest,
  ingestNew,
  getIngestRequests,
  getIngestionStatus,
  getIngestionErrors,
  getSkippedFiles,
  getStuckFiles,
  retryErrors,
  retrySkipped,
  retryStuck,
  getConnectors,
  createConnector,
} from "../api/ingestion";

const ACTIONS = {
  ingest: {
    label: "Ingest New Data",
    description:
      "Schedules a new ingest request using the most recent metadata. Existing documents are not removed.",
    btnClass: "btn-primary",
    confirm: false,
  },
  wipe: {
    label: "Wipe & Re-ingest",
    description:
      "Removes discovered files for the request's connector and schedules a full re-ingest from scratch.",
    btnClass: "btn-danger",
    confirm: true,
  },
};

// Layers highlighted as primary "files to ingest"
const FILE_LAYERS = new Set(["discovered_files", "loaded_files"]);

// All known layers in display order (mirrors backend _FULL_STATUS_QUERY stages)
const KNOWN_LAYERS = [
  "discovered_files",
  "loaded_files",
  "extracted_documents",
  "detected_documents (source)",
  "detected_documents (past-performance)",
  "detected_documents (resume)",
  "canonical_documents (past-performance)",
  "canonical_documents (resume)",
  "searchable_documents",
  "searchable_paragraphs",
  "searchable_tables",
  "searchable_images",
];

// Friendly names, matching ingest_monitor.py's LAYER_NAMES.
const LAYER_NAMES = {
  discovered_files: "Discovered Files",
  loaded_files: "Loaded Files",
  extracted_documents: "Extracted Documents",
  "detected_documents (source)": "Detected — Source",
  "detected_documents (past-performance)": "Detected — Past Perf.",
  "detected_documents (resume)": "Detected — Resume",
  "canonical_documents (past-performance)": "Canonical — Past Perf.",
  "canonical_documents (resume)": "Canonical — Resume",
  searchable_documents: "Searchable Documents",
  searchable_paragraphs: "Searchable Paragraphs",
  searchable_tables: "Searchable Tables",
  searchable_images: "Searchable Images",
};

const layerName = (l) => LAYER_NAMES[l] || l;

// Stages that only ever report an 'indexed' count: no per-row status, and no
// processed_timestamp, so the backend never returns a last-activity for them.
const SEARCHABLE_LAYERS = new Set([
  "searchable_documents",
  "searchable_paragraphs",
  "searchable_tables",
  "searchable_images",
]);

// Render order for the stacked bar, matching ingest_monitor.py's STATUS_ORDER so a
// stage reads left-to-right as work moving through it.
const STATUS_ORDER = ["new", "processing", "processed", "indexed", "error", "skipped"];

const STATUS_COLORS = {
  new: "#6e7681",
  processing: "#0d6efd",
  processed: "#198754",
  indexed: "#7048c4",
  error: "#dc3545",
  skipped: "#b8860b",
};

const AUTO_REFRESH_MS = 30_000; // 30 seconds

// Must match the value sent to /retry-stuck, or the reset acts on a different set of
// files than the table showed. Kept in one place for exactly that reason.
const STUCK_THRESHOLD_MINUTES = 10;

// Sentinel for the request picker's live option. The <select> value must be a
// string, and "" is what an unselected <option> yields, so live mode is "".
const LIVE = "";

function fmtRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 0) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function fmtTime(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function requestLabel(r) {
  const bits = [`#${r.ingest_request_id}`];
  if (r.connector_type) bits.push(r.connector_type);
  if (r.status) bits.push(r.status);
  if (r.file_count != null) bits.push(`${r.file_count.toLocaleString()} files`);
  const when = fmtRelative(r.last_activity || r.requested_at);
  if (when) bits.push(when);
  if (!r.actionable) bits.push("read-only");
  return bits.join(" · ");
}

function downloadCsv(rows, columns, filename) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(c.get(r))).join(","));
  const blob = new Blob([[header, ...body].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Group rows by layer, returned in pipeline order so panels read top to bottom.
function groupByLayer(rows) {
  const by = {};
  for (const r of rows) (by[r.layer] = by[r.layer] || []).push(r);
  const ordered = KNOWN_LAYERS.filter((l) => by[l]?.length).map((l) => [l, by[l]]);
  const extras = Object.keys(by)
    .filter((l) => !KNOWN_LAYERS.includes(l))
    .map((l) => [l, by[l]]);
  return [...ordered, ...extras];
}

// Stuck rows group by pipeline gap, in pipeline order.
const STUCK_STAGES = [
  "discovered → loaded",
  "loaded → extracted",
  "extracted → detected",
  "detected → indexed",
];

function groupByStage(rows) {
  const by = {};
  for (const r of rows) (by[r.stuck_at] = by[r.stuck_at] || []).push(r);
  return STUCK_STAGES.filter((st) => by[st]?.length).map((st) => [st, by[st]]);
}

function SummaryCard({ label, value, tone, hint }) {
  return (
    <div className="col-6 col-md-4 col-xl">
      <div className="border rounded h-100 py-2 px-2" title={hint}>
        <div className="text-muted" style={{ fontSize: "0.72rem" }}>{label}</div>
        <div className={`fw-bold text-${tone}`} style={{ fontSize: "1.6rem", lineHeight: 1.2 }}>
          {Number(value ?? 0).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function CollapsiblePanel({ panelKey, openKeys, setOpenKeys, header, badges, actions, children }) {
  const open = openKeys.has(panelKey);
  const toggle = () =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      next.has(panelKey) ? next.delete(panelKey) : next.add(panelKey);
      return next;
    });
  return (
    <div className="border-bottom">
      <div
        className="d-flex align-items-center gap-2 px-3 py-2"
        style={{ cursor: "pointer", background: "rgba(0,0,0,0.02)" }}
        onClick={toggle}
      >
        <span className="text-muted" style={{ fontSize: "0.7rem", width: "0.8rem" }}>
          {open ? "▼" : "►"}
        </span>
        <span className="fw-semibold small">{header}</span>
        {badges}
        <span className="ms-auto d-flex gap-2" onClick={(e) => e.stopPropagation()}>
          {actions}
        </span>
      </div>
      {open && <div style={{ overflowX: "auto" }}>{children}</div>}
    </div>
  );
}

export default function Ingestion() {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgsError, setOrgsError] = useState("");

  // Ingest request scope. selectedRequestId is the <select> value: LIVE ("") means
  // "track whichever request is latest"; anything else pins to that request id.
  const [requests, setRequests] = useState(null); // null = not loaded
  const [selectedRequestId, setSelectedRequestId] = useState(LIVE);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState("");

  // Action selection
  // confirmAction: which action is awaiting a second click. pendingAction: which is
  // currently running, so only that button shows a spinner.
  const [confirmAction, setConfirmAction] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null); // { action, time, ok, message }

  // Status table — never cleared once loaded; refreshes in-place
  const [statusRows, setStatusRows] = useState(null);
  const [unavailableLayers, setUnavailableLayers] = useState([]); // tables that failed to query
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [statusTime, setStatusTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [pipelineView, setPipelineView] = useState("cards");

  // Summary roll-up: counts computed in SQL by /status, so they stay exact even when
  // the row listings below are capped. null means the roll-up could not be computed —
  // rendered as "unavailable", never as zeros.
  const [summary, setSummary] = useState(null);

  // Scope resolved by the backend for the current view
  const [resolvedRid, setResolvedRid] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [actionable, setActionable] = useState(false);

  // Error files + classification rollup
  const [errorRows, setErrorRows] = useState(null);
  const [errorSummary, setErrorSummary] = useState([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorTruncated, setErrorTruncated] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);
  const [errorError, setErrorError] = useState("");
  const [errorSort, setErrorSort] = useState({ key: "error_at", dir: "desc" });
  const [errorLayerFilter, setErrorLayerFilter] = useState("");
  const [errorSearch, setErrorSearch] = useState("");

  // Skipped files
  const [skippedRows, setSkippedRows] = useState(null);
  const [skippedCounts, setSkippedCounts] = useState({});
  const [skippedTotal, setSkippedTotal] = useState(0);
  const [skippedTruncated, setSkippedTruncated] = useState(false);
  const [skippedLoading, setSkippedLoading] = useState(false);
  const [skippedError, setSkippedError] = useState("");

  // Stuck files — a stage finished them, the next stage never picked them up
  const [stuckRows, setStuckRows] = useState(null);
  const [stuckCounts, setStuckCounts] = useState({});
  const [stuckTruncated, setStuckTruncated] = useState(false);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [stuckError, setStuckError] = useState("");
  const [selectedStuck, setSelectedStuck] = useState(new Set()); // `${stuck_at}::${filepath}`
  const [stuckRetryResult, setStuckRetryResult] = useState(null);

  // Which layer panels are expanded (survives auto-refresh)
  const [openErrorPanels, setOpenErrorPanels] = useState(new Set());
  const [openSkipPanels, setOpenSkipPanels] = useState(new Set());
  const [openStuckPanels, setOpenStuckPanels] = useState(new Set());

  // Retry selections. Errors and skips are kept in separate sets so selecting rows
  // in one table never silently drags rows from the other into a retry.
  const [selectedErrors, setSelectedErrors] = useState(new Set()); // `${layer}::${filepath}`
  const [selectedSkips, setSelectedSkips] = useState(new Set());
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null); // { ok, message }
  const [skipRetryResult, setSkipRetryResult] = useState(null);

  // Status timestamps — { layer: ISO-string } from the status response
  const [statusTimestamps, setStatusTimestamps] = useState({});

  // Distinct layers present in current errorRows (for the filter dropdown)
  const errorLayerOptions = useMemo(() => {
    if (!errorRows) return [];
    return Array.from(new Set(errorRows.map((r) => r.layer))).sort();
  }, [errorRows]);

  // Filtered + sorted view of errorRows
  const displayErrorRows = useMemo(() => {
    if (!errorRows) return null;
    const needle = errorSearch.trim().toLowerCase();
    const filtered = errorRows.filter((r) => {
      if (errorLayerFilter && r.layer !== errorLayerFilter) return false;
      if (needle) {
        const hay = `${r.filepath ?? ""} ${r.error_message ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    const { key, dir } = errorSort;
    const mult = dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls always last
      if (bv == null) return -1;
      if (key === "error_at") {
        return (new Date(av).getTime() - new Date(bv).getTime()) * mult;
      }
      return String(av).localeCompare(String(bv)) * mult;
    };
    return [...filtered].sort(cmp);
  }, [errorRows, errorLayerFilter, errorSearch, errorSort]);

  const toggleErrorSort = (key) => {
    setErrorSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "error_at" ? "desc" : "asc" }
    );
  };
  const sortIndicator = (key) =>
    errorSort.key === key ? (errorSort.dir === "asc" ? " ▲" : " ▼") : "";

  // Connectors — no longer a scope control (the request picker carries connector
  // context). Loaded only to decide whether to offer the blob-connector form.
  const [connectors, setConnectors] = useState(null);
  const [showConnectorForm, setShowConnectorForm] = useState(false);
  const [connectionString, setConnectionString] = useState("");
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null); // { ok, message }

  const selectedOrgIdRef = useRef(selectedOrgId);
  selectedOrgIdRef.current = selectedOrgId;

  const selectedRequestIdRef = useRef(selectedRequestId);
  selectedRequestIdRef.current = selectedRequestId;

  useEffect(() => {
    getOrganizations()
      .then((data) => {
        const sorted = (data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(sorted);
      })
      .catch((err) => setOrgsError(err?.message || "Failed to load organizations"))
      .finally(() => setLoadingOrgs(false));
  }, []);

  // ridParam converts the picker value into the API argument: null for live mode.
  const ridParam = (value) => (value === LIVE ? null : Number(value));

  const fetchRequests = useCallback(async (orgId) => {
    if (!orgId) return;
    setRequestsLoading(true);
    setRequestsError("");
    try {
      const data = await getIngestRequests(Number(orgId));
      if (orgId === selectedOrgIdRef.current) {
        setRequests(data.requests || []);
        if (data.error) setRequestsError(data.error);
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setRequestsError(err?.message || "Failed to load ingest requests");
        setRequests([]);
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) setRequestsLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async (orgId, requestValue) => {
    if (!orgId) return;
    setStatusLoading(true);
    setStatusError("");
    try {
      const data = await getIngestionStatus(Number(orgId), ridParam(requestValue));
      if (orgId === selectedOrgIdRef.current) {
        setStatusRows(data.rows || []);
        setUnavailableLayers(data.skipped || []);
        setStatusTimestamps(data.timestamps || {});
        setSummary(data.summary ?? null);
        setResolvedRid(data.ingest_request_id ?? null);
        setIsLive(!!data.is_live);
        setActionable(!!data.actionable);
        setStatusTime(new Date());
        // The backend reports a read failure in-band rather than as a non-200, so a
        // dead tenant is never rendered as a healthy-but-empty pipeline.
        if (data.error) setStatusError(data.error);
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setStatusError(err?.message || "Failed to load ingestion status");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) setStatusLoading(false);
    }
  }, []);

  const fetchErrors = useCallback(async (orgId, requestValue) => {
    if (!orgId) return;
    setErrorLoading(true);
    setErrorError("");
    try {
      const data = await getIngestionErrors(Number(orgId), ridParam(requestValue));
      if (orgId === selectedOrgIdRef.current) {
        setErrorRows(data.rows || []);
        setErrorSummary(data.summary || []);
        setErrorTotal(data.total ?? (data.rows || []).length);
        setErrorTruncated(!!data.truncated);
        if (data.error) setErrorError(data.error);
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setErrorError(err?.message || "Failed to load ingestion errors");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) setErrorLoading(false);
    }
  }, []);

  const fetchSkipped = useCallback(async (orgId, requestValue) => {
    if (!orgId) return;
    setSkippedLoading(true);
    setSkippedError("");
    try {
      const data = await getSkippedFiles(Number(orgId), ridParam(requestValue));
      if (orgId === selectedOrgIdRef.current) {
        setSkippedRows(data.rows || []);
        setSkippedCounts(data.counts_by_reason || {});
        setSkippedTotal(data.total ?? (data.rows || []).length);
        setSkippedTruncated(!!data.truncated);
        if (data.error) setSkippedError(data.error);
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setSkippedError(err?.message || "Failed to load skipped files");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) setSkippedLoading(false);
    }
  }, []);

  const fetchStuck = useCallback(async (orgId, requestValue) => {
    if (!orgId) return;
    setStuckLoading(true);
    setStuckError("");
    try {
      const data = await getStuckFiles(
        Number(orgId), ridParam(requestValue), STUCK_THRESHOLD_MINUTES);
      if (orgId === selectedOrgIdRef.current) {
        setStuckRows(data.rows || []);
        setStuckCounts(data.counts_by_stage || {});
        setStuckTruncated(!!data.truncated);
        if (data.error) setStuckError(data.error);
        else if ((data.unavailable || []).length > 0) {
          setStuckError(`Could not check: ${data.unavailable.join(", ")}`);
        }
      }
    } catch (err) {
      if (orgId === selectedOrgIdRef.current) {
        setStuckError(err?.message || "Failed to load stuck files");
      }
    } finally {
      if (orgId === selectedOrgIdRef.current) setStuckLoading(false);
    }
  }, []);

  const fetchConnectors = useCallback(async (orgId) => {
    if (!orgId) return;
    try {
      const data = await getConnectors(Number(orgId));
      if (orgId === selectedOrgIdRef.current) setConnectors(data.connectors || []);
    } catch {
      if (orgId === selectedOrgIdRef.current) setConnectors([]);
    }
  }, []);

  async function handleCreateConnector() {
    if (!selectedOrgId || !connectionString.trim()) return;
    setCreating(true);
    setCreateResult(null);
    try {
      await createConnector(Number(selectedOrgId), connectionString.trim());
      setCreateResult({ ok: true, message: "Connector created successfully." });
      setConnectionString("");
      fetchConnectors(selectedOrgId);
    } catch (err) {
      setCreateResult({ ok: false, message: err?.message || "Failed to create connector" });
    } finally {
      setCreating(false);
    }
  }

  // When org changes: clear everything and reload requests + connectors.
  useEffect(() => {
    if (!selectedOrgId) return;
    setStatusRows(null);
    setUnavailableLayers([]);
    setStatusTimestamps({});
    setStatusError("");
    setStatusTime(null);
    setSummary(null);
    setErrorRows(null);
    setErrorSummary([]);
    setErrorTotal(0);
    setErrorTruncated(false);
    setErrorError("");
    setSkippedRows(null);
    setSkippedCounts({});
    setSkippedTotal(0);
    setSkippedTruncated(false);
    setSkippedError("");
    setStuckRows(null);
    setStuckCounts({});
    setStuckTruncated(false);
    setStuckError("");
    setSelectedStuck(new Set());
    setStuckRetryResult(null);
    setSelectedErrors(new Set());
    setSelectedSkips(new Set());
    setRetryResult(null);
    setSkipRetryResult(null);
    setRequests(null);
    setRequestsError("");
    setSelectedRequestId(LIVE);
    setResolvedRid(null);
    setIsLive(true);
    setActionable(false);
    setConnectors(null);
    setShowConnectorForm(false);
    setCreateResult(null);
    setConnectionString("");
    fetchRequests(selectedOrgId);
    fetchConnectors(selectedOrgId);
  }, [selectedOrgId, fetchRequests, fetchConnectors]);

  // Load pipeline data for the selected org + request scope.
  useEffect(() => {
    if (!selectedOrgId) return;
    fetchStatus(selectedOrgId, selectedRequestId);
    fetchErrors(selectedOrgId, selectedRequestId);
    fetchSkipped(selectedOrgId, selectedRequestId);
    fetchStuck(selectedOrgId, selectedRequestId);
  }, [selectedOrgId, selectedRequestId, fetchStatus, fetchErrors, fetchSkipped, fetchStuck]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh || !selectedOrgId) return;
    const id = setInterval(() => {
      fetchStatus(selectedOrgIdRef.current, selectedRequestIdRef.current);
      fetchErrors(selectedOrgIdRef.current, selectedRequestIdRef.current);
      fetchSkipped(selectedOrgIdRef.current, selectedRequestIdRef.current);
      fetchStuck(selectedOrgIdRef.current, selectedRequestIdRef.current);
      // In live mode the newest request can change under us, and every row's
      // relative timestamp drifts, so the picker needs refreshing too.
      fetchRequests(selectedOrgIdRef.current);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, selectedOrgId, fetchStatus, fetchErrors, fetchSkipped, fetchStuck, fetchRequests]);

  function refreshAll() {
    fetchStatus(selectedOrgId, selectedRequestId);
    fetchErrors(selectedOrgId, selectedRequestId);
    fetchSkipped(selectedOrgId, selectedRequestId);
    fetchStuck(selectedOrgId, selectedRequestId);
    fetchRequests(selectedOrgId);
  }

  const selectedOrgName = orgs.find((o) => String(o.id) === selectedOrgId)?.name || "";
  const currentRequest = (requests || []).find((r) => r.ingest_request_id === resolvedRid) || null;
  // Mutations target the connector behind the request in view, not whichever
  // connector the backend happens to consider "preferred".
  const scopeConnectorId = currentRequest?.connector_id ?? null;
  const hasBlobConnector = (connectors || []).some((c) => c.type === "azure-blob-storage");
  // Writes are refused server-side for older requests; mirror that in the UI.
  //
  // scopeConnectorId comes from /requests while `actionable` comes from /status — two
  // independent fetches. Without the connector clause below, /status landing first (or
  // /requests failing) leaves the buttons live with no connector, and the backend would
  // fall back to its "preferred" connector: an operator looking at one connector's
  // request could wipe a different connector's entire corpus.
  const writesAllowed = !!selectedOrgId && actionable && scopeConnectorId != null;
  const writeBlockedReason = !selectedOrgId
    ? "Select an organization"
    : !actionable
    ? "Only the most recent ingest requests accept writes"
    : scopeConnectorId == null
    ? "Still resolving which connector this request belongs to"
    : null;

  const errKey = (r) => `${r.layer}::${r.filepath}`;
  // Only retryable rows are selectable — re-running a file whose extension has no
  // extractor, or whose content exceeds a hard model limit, reproduces the same error.
  const retryableErrors = (errorRows || []).filter((r) => r.retryable);
  const selectedRetryableCount = retryableErrors.filter((r) =>
    selectedErrors.has(errKey(r))
  ).length;

  const retryableSkips = (skippedRows || []).filter((r) => r.retryable);
  const selectedRetryableSkipCount = retryableSkips.filter((r) =>
    selectedSkips.has(errKey(r))
  ).length;

  const toggleIn = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleErrorKey = toggleIn(setSelectedErrors);
  const toggleSkipKey = toggleIn(setSelectedSkips);
  const toggleStuckKey = toggleIn(setSelectedStuck);

  // Stuck rows are keyed by gap, not layer: the same file can be stuck at only one
  // gap, but the label is what the reset dispatches on.
  const stuckKey = (r) => `${r.stuck_at}::${r.filepath}`;
  const stuckTotal = Object.values(stuckCounts).reduce((a, b) => a + b, 0);

  // Every retry names the request currently on screen rather than letting the server
  // fall back to "latest" — by the time the click lands, latest may be a new request.
  function retryMessage(data) {
    const parts = [`${data.reset_count} file(s) re-queued.`];
    if (data.dropped_non_retryable > 0) {
      parts.push(`${data.dropped_non_retryable} skipped as not retryable.`);
    }
    if (data.reset_count === 0) {
      parts.push("Nothing matched — the rows may have changed since this view loaded.");
    }
    return parts.join(" ");
  }

  async function runRetry(kind, opts) {
    const fn = kind === "skip" ? retrySkipped : retryErrors;
    const setResult = kind === "skip" ? setSkipRetryResult : setRetryResult;
    const clearSel = kind === "skip" ? setSelectedSkips : setSelectedErrors;
    setRetrying(true);
    setResult(null);
    try {
      const data = await fn(Number(selectedOrgId), { ingestRequestId: resolvedRid, ...opts });
      setResult({ ok: true, message: retryMessage(data) });
      clearSel(new Set());
      refreshAll();
    } catch (err) {
      setResult({ ok: false, message: err?.message || "Retry failed" });
    } finally {
      setRetrying(false);
    }
  }

  const toRetryRow = (r) => ({
    layer: r.layer,
    filepath: r.filepath,
    error_message: r.error_message ?? r.reason_message ?? null,
    error_at: r.error_at ?? r.skipped_at ?? null,
  });

  async function handleAction(key) {
    if (!selectedOrgId || !writesAllowed || busy) return;
    const action = ACTIONS[key];

    // Wipe deletes tenant data, so it takes a second click. Ingest New only queues a
    // request — it stays one click, as it has been.
    if (action.confirm && confirmAction !== key) {
      setConfirmAction(key);
      return;
    }

    setConfirmAction(null);
    setPendingAction(key);
    setBusy(true);
    try {
      let msg;
      if (key === "wipe") {
        const data = await wipeReingest(Number(selectedOrgId), scopeConnectorId);
        // deleted_rows is the whole pipeline chain, not just discovered_files, so name
        // the stages that actually lost rows rather than a bare total.
        const byTable = Object.entries(data.deleted_by_table || {}).filter(([, n]) => n > 0);
        const detail = byTable.length
          ? ` (${byTable.map(([t, n]) => `${t} ${n.toLocaleString()}`).join(", ")})`
          : "";
        msg = `Deleted ${(data.deleted_rows ?? 0).toLocaleString()} row(s) across the pipeline${detail}. Re-ingest scheduled.`;
      } else {
        await ingestNew(Number(selectedOrgId), scopeConnectorId);
        msg = "Ingest request submitted successfully.";
      }
      setLastRun({ action: action.label, time: new Date(), ok: true, message: msg });
      // A new request was just queued; snap back to live so the view follows it.
      setSelectedRequestId(LIVE);
      refreshAll();
    } catch (err) {
      setLastRun({
        action: action.label,
        time: new Date(),
        ok: false,
        message: err?.message || "Operation failed",
      });
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  }

  function handleRetrySelected() {
    if (!selectedOrgId || selectedRetryableCount === 0 || !writesAllowed) return;
    runRetry("error", {
      rows: retryableErrors.filter((r) => selectedErrors.has(errKey(r))).map(toRetryRow),
    });
  }

  function handleRetrySkipSelected() {
    if (!selectedOrgId || selectedRetryableSkipCount === 0 || !writesAllowed) return;
    runRetry("skip", {
      rows: retryableSkips.filter((r) => selectedSkips.has(errKey(r))).map(toRetryRow),
    });
  }

  async function runStuckRetry(items) {
    setRetrying(true);
    setStuckRetryResult(null);
    try {
      const data = await retryStuck(Number(selectedOrgId), {
        ingestRequestId: resolvedRid,
        items,
        thresholdMinutes: STUCK_THRESHOLD_MINUTES,
      });
      setStuckRetryResult({ ok: true, message: retryMessage(data) });
      setSelectedStuck(new Set());
      refreshAll();
    } catch (err) {
      setStuckRetryResult({ ok: false, message: err?.message || "Retry failed" });
    } finally {
      setRetrying(false);
    }
  }

  // Bulk: let the server pick the rows. It filters with the SQL mirror of the same
  // classifier in one UPDATE, which also avoids the read-committed race a
  // select-then-update would introduce.
  const handleRetryAllInLayer = (kind, layer) => runRetry(kind, { layers: [layer] });

  // Build status table data
  const layerMap = {};
  for (const row of statusRows || []) {
    if (!layerMap[row.layer]) layerMap[row.layer] = {};
    layerMap[row.layer][row.status] = row.count;
  }
  const unavailableSet = new Set(unavailableLayers);
  const knownSet = new Set(KNOWN_LAYERS);
  const extraLayers = Object.keys(layerMap).filter((l) => !knownSet.has(l));
  const layers = statusRows !== null ? [...KNOWN_LAYERS, ...extraLayers] : [];
  const allStatuses = [...new Set((statusRows || []).map((r) => r.status))].sort();

  const ridSuffix = resolvedRid != null ? `-req${resolvedRid}` : "";
  const orgSlug = selectedOrgName || selectedOrgId || "org";

  return (
    <div className="row g-3">
      {/* ── Scope toolbar ───────────────────────────────────────────────── */}
      <div className="col-12">
        <div className="card shadow-sm">
          <div className="card-body py-2">
            <div className="d-flex flex-wrap align-items-end gap-3">
              <div style={{ minWidth: "14rem" }}>
                <label className="form-label small fw-semibold mb-1">Organization</label>
                {loadingOrgs ? (
                  <div className="text-muted small">Loading…</div>
                ) : orgsError ? (
                  <div className="text-danger small">{orgsError}</div>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={selectedOrgId}
                    onChange={(e) => {
                      setSelectedOrgId(e.target.value);
                      setLastRun(null);
                      setConfirmAction(null);
                    }}
                  >
                    <option value="">— Select an organization —</option>
                    {orgs.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ minWidth: "24rem", flex: "1 1 24rem" }}>
                <label className="form-label small fw-semibold mb-1">
                  Ingest request
                  {resolvedRid != null && (
                    <span className="text-muted fw-normal ms-2">
                      showing #{resolvedRid}
                      {isLive && " (live)"}
                    </span>
                  )}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={selectedRequestId}
                  disabled={!selectedOrgId || requests === null}
                  onChange={(e) => {
                    setSelectedRequestId(e.target.value);
                    setSelectedErrors(new Set());
                    setSelectedSkips(new Set());
                    setSelectedStuck(new Set());
                    setRetryResult(null);
                    setSkipRetryResult(null);
                    setStuckRetryResult(null);
                    setConfirmAction(null);
                  }}
                >
                  <option value={LIVE}>Live — track the latest request</option>
                  {(requests || []).map((r) => (
                    <option key={r.ingest_request_id} value={String(r.ingest_request_id)}>
                      {requestLabel(r)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-2 ms-auto">
                {statusTime && (
                  <span className="text-muted small">
                    updated {statusTime.toLocaleTimeString()}
                    {statusLoading && " ↻"}
                  </span>
                )}
                <button
                  className={`btn btn-sm ${autoRefresh ? "btn-success" : "btn-outline-secondary"}`}
                  disabled={!selectedOrgId}
                  onClick={() => setAutoRefresh((v) => !v)}
                  title="Re-poll status, errors, skips and the request list every 30 seconds"
                >
                  {autoRefresh ? "Live: On" : "Live: Off"}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!selectedOrgId || statusLoading}
                  onClick={refreshAll}
                >
                  Refresh
                </button>
                <span className="vr mx-1" />
                {Object.entries(ACTIONS).map(([key, meta]) => (
                  <button
                    key={key}
                    className={`btn btn-sm ${meta.btnClass}`}
                    disabled={!writesAllowed || busy}
                    onClick={() => handleAction(key)}
                    title={
                      writesAllowed
                        ? `${meta.description}${
                            currentRequest?.connector_name
                              ? ` Targets connector ${currentRequest.connector_name} (${currentRequest.connector_type}).`
                              : ""
                          }`
                        : writeBlockedReason
                    }
                  >
                    {busy && pendingAction === key
                      ? "Running…"
                      : confirmAction === key
                      ? "Confirm?"
                      : meta.label}
                  </button>
                ))}
              </div>
            </div>

            {confirmAction && (
              <div className="alert alert-warning py-1 px-2 small mt-2 mb-0 d-flex align-items-center gap-2">
                <span>
                  {ACTIONS[confirmAction].label} for <strong>{selectedOrgName}</strong>
                  {currentRequest?.connector_name && (
                    <> on connector <strong>{currentRequest.connector_name}</strong></>
                  )}
                  ? {ACTIONS[confirmAction].description}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary ms-auto py-0"
                  onClick={() => setConfirmAction(null)}
                >
                  Cancel
                </button>
              </div>
            )}

            {lastRun && (
              <div
                className={`alert py-1 px-2 small mt-2 mb-0 ${
                  lastRun.ok ? "alert-success" : "alert-danger"
                }`}
                role="alert"
              >
                <strong>{lastRun.action}</strong> at {lastRun.time.toLocaleTimeString()} —{" "}
                {lastRun.message}
              </div>
            )}

            {requestsError && <div className="text-danger small mt-2">{requestsError}</div>}
            {selectedOrgId && requests !== null && requests.length === 0 && !requestsError && (
              <div className="text-muted small fst-italic mt-2">
                This organization has no ingest requests yet.
              </div>
            )}
            {requestsLoading && requests === null && (
              <div className="text-muted small mt-2">Loading ingest requests…</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Read-only banner for historical requests ─────────────────────── */}
      {resolvedRid != null && !actionable && (
        <div className="col-12">
          <div className="alert alert-secondary py-2 px-3 small mb-0">
            Viewing <strong>request #{resolvedRid}</strong>, which is not one of the most
            recent ingest requests. Wipe, re-ingest and retry are disabled — the server
            rejects writes against older requests.
          </div>
        </div>
      )}

      {/* ── Summary cards ────────────────────────────────────────────────── */}
      {selectedOrgId && resolvedRid != null && (
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold">
                Ingestion Summary
                <span className="text-muted fw-normal small ms-2">request #{resolvedRid}</span>
              </span>
              {summary === null ? (
                <span className="badge bg-secondary">unavailable</span>
              ) : !summary.has_data ? (
                <span className="badge bg-secondary">no data</span>
              ) : summary.pipeline_complete ? (
                <span className="badge bg-success">complete</span>
              ) : (
                <span className="badge bg-primary">in progress</span>
              )}
            </div>
            <div className="card-body py-3">
              {summary === null ? (
                <div className="text-muted small fst-italic">
                  The summary roll-up could not be computed for this request. The per-stage
                  counts below are still accurate.
                </div>
              ) : (
                <div className="row g-3 text-center">
                  <SummaryCard label="Successfully Ingested" value={summary.ingested_count} tone="success"
                    hint="Source documents processed and present in the search index" />
                  <SummaryCard label="Errored Files" value={summary.errored_count} tone="danger"
                    hint="Unique files that failed and did not ultimately make it in" />
                  <SummaryCard label="Skipped Files" value={summary.skipped_count} tone="warning"
                    hint="Unique files the pipeline declined and that never made it in" />
                  <SummaryCard label="Stuck in Pipeline" value={stuckTotal} tone="primary"
                    hint={`Finished one stage but absent from the next for over ${STUCK_THRESHOLD_MINUTES} min`} />
                  <SummaryCard label="In Processing" value={summary.inprogress_count} tone="info"
                    hint="Unique files a worker is actively holding right now" />
                </div>
              )}
              {summary !== null && summary.has_data && !summary.pipeline_complete && (
                <p className="text-muted small fst-italic mb-0 mt-3">
                  The pipeline is still working: {summary.active_count.toLocaleString()} row(s)
                  queued or in flight. These counts will keep moving until it settles.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Pipeline ─────────────────────────────────────────────────────── */}
      <div className="col-12">
        <div className="card shadow-sm">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              Pipeline
              {resolvedRid != null && (
                <span className="text-muted fw-normal small ms-2">request #{resolvedRid}</span>
              )}
            </span>
            <div className="btn-group btn-group-sm" role="group" aria-label="Pipeline view">
              <button
                type="button"
                className={`btn ${pipelineView === "cards" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setPipelineView("cards")}
              >
                Cards
              </button>
              <button
                type="button"
                className={`btn ${pipelineView === "table" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setPipelineView("table")}
              >
                Table
              </button>
            </div>
          </div>
          <div className="card-body" style={{ maxHeight: "34rem", overflowY: "auto" }}>
            {!selectedOrgId && (
              <div className="text-muted small fst-italic">
                Select an organization to view its ingestion pipeline.
              </div>
            )}
            {selectedOrgId && statusRows === null && statusLoading && (
              <div className="text-muted small">Loading status…</div>
            )}
            {statusError && statusRows === null && (
              <div className="text-danger small">{statusError}</div>
            )}
            {selectedOrgId && statusRows !== null && resolvedRid == null && !statusError && (
              <div className="text-muted small fst-italic">No ingest request to report on yet.</div>
            )}
            {statusRows !== null && resolvedRid != null && (
              <>
                {statusError && <div className="text-danger small mb-2">{statusError}</div>}
                {unavailableLayers.length > 0 && (
                  <div className="text-warning small mb-2">
                    Unavailable (table missing or query timed out): {unavailableLayers.join(", ")}
                  </div>
                )}

                {pipelineView === "cards" ? (
                  <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xxl-5 g-2">
                    {layers.map((layer) => {
                      const counts = layerMap[layer] || {};
                      const isUnavailable = unavailableSet.has(layer) || !layerMap[layer];
                      const total = Object.values(counts).reduce((a, b) => a + b, 0);
                      const hasError = (counts.error || 0) > 0;
                      const isSearchable = SEARCHABLE_LAYERS.has(layer);
                      const ts = statusTimestamps[layer];
                      return (
                        <div className="col" key={layer}>
                          <div
                            className="border rounded p-2 h-100"
                            style={{
                              borderColor: hasError ? STATUS_COLORS.error : undefined,
                              borderWidth: hasError ? 2 : undefined,
                              opacity: isUnavailable ? 0.55 : 1,
                            }}
                          >
                            <div className="text-muted text-truncate" style={{ fontSize: "0.72rem" }}
                                 title={layer}>
                              {layerName(layer)}
                            </div>
                            <div className="fw-bold" style={{ fontSize: "1.35rem", lineHeight: 1.2 }}>
                              {isUnavailable ? "—" : total.toLocaleString()}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.68rem" }}>
                              {/* searchable_* rows carry no processed_timestamp, so the
                                  backend never reports one for them — say so rather than
                                  showing a bare dash that reads as "nothing happened". */}
                              {isUnavailable
                                ? "unavailable"
                                : ts
                                ? `Last activity: ${fmtTime(ts)}`
                                : isSearchable
                                ? "No per-row timestamp at this stage"
                                : "No activity recorded"}
                            </div>

                            <div
                              className="d-flex rounded overflow-hidden mt-2 mb-1"
                              style={{ height: 6, background: "#e9ecef" }}
                            >
                              {!isUnavailable &&
                                total > 0 &&
                                STATUS_ORDER.filter((st) => (counts[st] || 0) > 0).map((st) => (
                                  <div
                                    key={st}
                                    style={{
                                      width: `${((counts[st] / total) * 100).toFixed(1)}%`,
                                      background: STATUS_COLORS[st] || STATUS_COLORS.new,
                                    }}
                                    title={`${counts[st].toLocaleString()} ${st}`}
                                  />
                                ))}
                            </div>

                            <div className="d-flex flex-wrap gap-1">
                              {isUnavailable ? (
                                <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                                  not queryable
                                </span>
                              ) : total === 0 ? (
                                <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                                  0 records
                                </span>
                              ) : (
                                STATUS_ORDER.filter((st) => (counts[st] || 0) > 0).map((st) => (
                                  <span
                                    key={st}
                                    className="badge rounded-pill"
                                    style={{
                                      fontSize: "0.62rem",
                                      fontWeight: 500,
                                      color: STATUS_COLORS[st] || STATUS_COLORS.new,
                                      background: `${STATUS_COLORS[st] || STATUS_COLORS.new}22`,
                                    }}
                                  >
                                    {counts[st].toLocaleString()} {st}
                                  </span>
                                ))
                              )}
                              {/* Any status the backend returns that this palette does
                                  not know about must still be visible, not dropped. */}
                              {!isUnavailable &&
                                Object.keys(counts)
                                  .filter((st) => !STATUS_ORDER.includes(st))
                                  .map((st) => (
                                    <span key={st} className="badge rounded-pill bg-dark-subtle text-dark-emphasis"
                                          style={{ fontSize: "0.62rem", fontWeight: 500 }}>
                                      {counts[st].toLocaleString()} {st}
                                    </span>
                                  ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Layer</th>
                          {allStatuses.map((s) => (
                            <th key={s} className="text-end">{s}</th>
                          ))}
                          <th className="text-end">total</th>
                          <th className="text-end">last activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {layers.map((layer) => {
                          const counts = layerMap[layer];
                          const isUnavailable = unavailableSet.has(layer) || !counts;
                          const total = isUnavailable
                            ? null
                            : Object.values(counts).reduce((a, b) => a + b, 0);
                          return (
                            <tr key={layer} className={FILE_LAYERS.has(layer) ? "table-info" : ""}>
                              <td className="font-monospace small">{layer}</td>
                              {isUnavailable ? (
                                <td
                                  colSpan={allStatuses.length + 1}
                                  className="text-muted text-end small fst-italic"
                                >
                                  —
                                </td>
                              ) : (
                                <>
                                  {allStatuses.map((s) => (
                                    <td key={s} className="text-end">
                                      {counts[s] != null ? counts[s].toLocaleString() : "—"}
                                    </td>
                                  ))}
                                  <td className="text-end fw-semibold">{total.toLocaleString()}</td>
                                </>
                              )}
                              <td className="text-end text-muted"
                                  style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                {statusTimestamps[layer] ? fmtTime(statusTimestamps[layer]) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* ── Error summary ────────────────────────────────────────────────── */}
      {errorSummary.length > 0 && (
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Error Summary</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() =>
                  downloadCsv(
                    errorSummary,
                    [
                      { label: "Stage", get: (r) => layerName(r.layer) },
                      { label: "Error Type", get: (r) => r.error_type },
                      { label: "Count", get: (r) => r.count },
                      { label: "Retryable", get: (r) => (r.retryable ? "Yes" : "No") },
                    ],
                    `error-summary-${orgSlug}${ridSuffix}.csv`
                  )
                }
              >
                Export CSV
              </button>
            </div>
            <div className="card-body p-0">
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Stage</th>
                    <th>Error Type</th>
                    <th className="text-end">Count</th>
                    <th className="text-center">Retryable</th>
                  </tr>
                </thead>
                <tbody>
                  {errorSummary.map((r, i) => (
                    <tr key={i}>
                      <td className="small">{layerName(r.layer)}</td>
                      <td className="small">{r.error_type}</td>
                      <td className="text-end small">{r.count.toLocaleString()}</td>
                      <td
                        className={`text-center small fw-semibold ${
                          r.retryable ? "text-success" : "text-muted"
                        }`}
                      >
                        {r.retryable ? "Yes" : "No"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Stuck files ──────────────────────────────────────────────────── */}
      <div className="col-12">
        <div className="card shadow-sm border-primary border-opacity-25">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              Stuck Files
              {stuckRows !== null && (
                <span className="text-muted fw-normal small ms-2">
                  {stuckTotal} file{stuckTotal !== 1 ? "s" : ""} sitting in a pipeline gap
                  {` for over ${STUCK_THRESHOLD_MINUTES} min`}
                  {stuckTruncated && ` · showing first ${stuckRows.length}`}
                </span>
              )}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {stuckLoading && <span className="text-muted small">Loading…</span>}
              {stuckRows !== null && stuckTotal > 0 && (
                <>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      downloadCsv(
                        stuckRows,
                        [
                          { label: "File", get: (r) => r.filepath },
                          { label: "Stuck Between", get: (r) => r.stuck_at },
                          { label: "Stage Finished At", get: (r) => fmtTime(r.stage_processed_at) },
                        ],
                        `stuck-files-${orgSlug}${ridSuffix}.csv`
                      )
                    }
                  >
                    Export CSV
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    disabled={selectedStuck.size === 0 || retrying || stuckLoading || !writesAllowed}
                    onClick={() =>
                      runStuckRetry(
                        stuckRows
                          .filter((r) => selectedStuck.has(stuckKey(r)))
                          .map((r) => ({ filepath: r.filepath, stuck_at: r.stuck_at }))
                      )
                    }
                  >
                    {retrying
                      ? "Retrying…"
                      : `Retry Selected${selectedStuck.size > 0 ? ` (${selectedStuck.size})` : ""}`}
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={retrying || stuckLoading || !writesAllowed}
                    onClick={() => runStuckRetry(null)}
                    title={
                      writesAllowed
                        ? `Reset all ${stuckTotal} stuck files, including any beyond the listed page`
                        : "Retry is only permitted on the most recent ingest requests"
                    }
                  >
                    Retry All Stuck ({stuckTotal})
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="card-body p-0" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {stuckRetryResult && (
              <div
                className={`px-3 pt-2 small ${stuckRetryResult.ok ? "text-success" : "text-danger"}`}
              >
                {stuckRetryResult.message}
              </div>
            )}
            {!selectedOrgId && (
              <div className="p-3 text-muted small fst-italic">
                Select an organization to check for stuck files.
              </div>
            )}
            {selectedOrgId && (stuckRows === null || stuckLoading) && !stuckError && (
              <div className="p-3 text-muted small">Loading…</div>
            )}
            {stuckError && <div className="p-3 text-danger small">{stuckError}</div>}
            {stuckRows !== null && !stuckLoading && stuckTotal === 0 && !stuckError && (
              <div className="p-3 text-success small">
                No files stuck between stages. Every processed file has a row in the next stage.
              </div>
            )}
            {stuckTruncated && (
              <div className="px-3 pt-2 text-warning small">
                Too many stuck files to list them all. The counts and “Retry All Stuck” cover
                every file; the table shows the first {stuckRows.length}.
              </div>
            )}
            {stuckRows !== null &&
              stuckRows.length > 0 &&
              groupByStage(stuckRows).map(([stage, rows]) => {
                const key = `stuck::${stage}`;
                const selectedHere = rows.filter((r) => selectedStuck.has(stuckKey(r))).length;
                const allSelected = rows.length > 0 && selectedHere === rows.length;
                return (
                  <CollapsiblePanel
                    key={key}
                    panelKey={key}
                    openKeys={openStuckPanels}
                    setOpenKeys={setOpenStuckPanels}
                    header={stage}
                    badges={
                      <>
                        <span className="badge bg-primary-subtle text-primary-emphasis">
                          {stuckCounts[stage] ?? rows.length}
                        </span>
                        {selectedHere > 0 && (
                          <span className="badge bg-warning-subtle text-warning-emphasis">
                            {selectedHere} selected
                          </span>
                        )}
                      </>
                    }
                    actions={
                      <button
                        className="btn btn-outline-secondary btn-sm py-0"
                        onClick={() =>
                          setSelectedStuck((prev) => {
                            const next = new Set(prev);
                            rows.forEach((r) =>
                              allSelected ? next.delete(stuckKey(r)) : next.add(stuckKey(r))
                            );
                            return next;
                          })
                        }
                      >
                        {allSelected ? "Deselect all" : "Select listed"}
                      </button>
                    }
                  >
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "32px" }} />
                          <th style={{ minWidth: "260px" }}>File</th>
                          <th style={{ minWidth: "200px" }}>Finished Upstream At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => {
                          const k = stuckKey(row);
                          return (
                            <tr
                              key={i}
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleStuckKey(k)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedStuck.has(k)}
                                  onChange={() => toggleStuckKey(k)}
                                />
                              </td>
                              <td
                                className="font-monospace small"
                                style={{ wordBreak: "break-word" }}
                              >
                                {row.filepath}
                              </td>
                              <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                                {fmtTime(row.stage_processed_at)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CollapsiblePanel>
                );
              })}
          </div>
        </div>
      </div>

      {/* ── Errored files, grouped by stage ──────────────────────────────── */}
      <div className="col-12">
        <div className="card shadow-sm border-danger border-opacity-25">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              Errored Files
              {errorRows !== null && (
                <span className="text-muted fw-normal small ms-2">
                  {errorTotal} error row{errorTotal !== 1 ? "s" : ""}
                  {errorTruncated && ` · showing first ${errorRows.length}`}
                  {displayErrorRows && (errorLayerFilter || errorSearch.trim()) &&
                    ` · ${displayErrorRows.length} match filter`}
                </span>
              )}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {errorLoading && <span className="text-muted small">Loading…</span>}
              {errorRows !== null && errorRows.length > 0 && (
                <>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    style={{ width: "220px" }}
                    placeholder="Search file or reason…"
                    value={errorSearch}
                    onChange={(e) => setErrorSearch(e.target.value)}
                  />
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={errorLayerFilter}
                    onChange={(e) => setErrorLayerFilter(e.target.value)}
                  >
                    <option value="">All layers</option>
                    {errorLayerOptions.map((l) => (
                      <option key={l} value={l}>{layerName(l)}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      downloadCsv(
                        displayErrorRows ?? errorRows,
                        [
                          { label: "Stage", get: (r) => layerName(r.layer) },
                          { label: "File", get: (r) => r.filepath },
                          { label: "Error Type", get: (r) => r.error_type },
                          { label: "Retryable", get: (r) => (r.retryable ? "Yes" : "No") },
                          { label: "Error Message", get: (r) => r.error_message || "" },
                          { label: "Failed At", get: (r) => fmtTime(r.error_at) },
                        ],
                        `errored-files-${orgSlug}${ridSuffix}.csv`
                      )
                    }
                  >
                    Export CSV
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    disabled={
                      selectedRetryableCount === 0 || retrying || errorLoading || !writesAllowed
                    }
                    onClick={handleRetrySelected}
                    title={
                      writesAllowed
                        ? undefined
                        : "Retry is only permitted on the most recent ingest requests"
                    }
                  >
                    {retrying
                      ? "Retrying…"
                      : `Retry Selected${
                          selectedRetryableCount > 0 ? ` (${selectedRetryableCount})` : ""
                        }`}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="card-body p-0" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {retryResult && (
              <div className={`px-3 pt-2 small ${retryResult.ok ? "text-success" : "text-danger"}`}>
                {retryResult.message}
              </div>
            )}
            {!selectedOrgId && (
              <div className="p-3 text-muted small fst-italic">
                Select an organization to view errors.
              </div>
            )}
            {selectedOrgId && (errorRows === null || errorLoading) && !errorError && (
              <div className="p-3 text-muted small">Loading…</div>
            )}
            {errorError && <div className="p-3 text-danger small">{errorError}</div>}
            {errorRows !== null && !errorLoading && errorRows.length === 0 && !errorError && (
              <div className="p-3 text-success small">No errors in this request.</div>
            )}
            {errorRows !== null && errorRows.length > 0 && displayErrorRows.length === 0 && (
              <div className="p-3 text-muted small">No errors match the current filter.</div>
            )}
            {errorRows !== null &&
              errorRows.length > 0 &&
              groupByLayer(displayErrorRows).map(([layer, rows]) => {
                const key = `err::${layer}`;
                const retryable = rows.filter((r) => r.retryable);
                const selectedHere = retryable.filter((r) =>
                  selectedErrors.has(errKey(r))
                ).length;
                const allSelected = retryable.length > 0 && selectedHere === retryable.length;
                // Retry All resets the whole stage server-side, filter or not.
                const stageRetryable = errorRows.filter((r) => r.layer === layer && r.retryable).length;
                return (
                  <CollapsiblePanel
                    key={key}
                    panelKey={key}
                    openKeys={openErrorPanels}
                    setOpenKeys={setOpenErrorPanels}
                    header={layerName(layer)}
                    badges={
                      <>
                        <span className="badge bg-danger-subtle text-danger-emphasis">
                          {rows.length}
                        </span>
                        {retryable.length > 0 && (
                          <span className="badge bg-secondary-subtle text-secondary-emphasis">
                            {retryable.length} retryable
                          </span>
                        )}
                        {selectedHere > 0 && (
                          <span className="badge bg-warning-subtle text-warning-emphasis">
                            {selectedHere} selected
                          </span>
                        )}
                      </>
                    }
                    actions={
                      retryable.length > 0 && (
                        <>
                          <button
                            className="btn btn-outline-secondary btn-sm py-0"
                            onClick={() =>
                              setSelectedErrors((prev) => {
                                const next = new Set(prev);
                                retryable.forEach((r) =>
                                  allSelected ? next.delete(errKey(r)) : next.add(errKey(r))
                                );
                                return next;
                              })
                            }
                          >
                            {allSelected ? "Deselect all" : "Select retryable"}
                          </button>
                          <button
                            className="btn btn-warning btn-sm py-0"
                            disabled={retrying || !writesAllowed}
                            onClick={() => handleRetryAllInLayer("error", layer)}
                            title={
                              writesAllowed
                                ? "Reset every retryable error in this stage"
                                : "Retry is only permitted on the most recent ingest requests"
                            }
                          >
                            Retry All ({stageRetryable})
                          </button>
                        </>
                      )
                    }
                  >
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "32px" }} />
                          <th style={{ minWidth: "220px", cursor: "pointer", userSelect: "none" }} onClick={() => toggleErrorSort("filepath")}>
                            File{sortIndicator("filepath")}
                          </th>
                          <th style={{ minWidth: "160px", cursor: "pointer", userSelect: "none" }} onClick={() => toggleErrorSort("error_type")}>
                            Error Type{sortIndicator("error_type")}
                          </th>
                          <th style={{ minWidth: "90px" }} className="text-center">
                            Retryable
                          </th>
                          <th style={{ minWidth: "280px", cursor: "pointer", userSelect: "none" }} onClick={() => toggleErrorSort("error_message")}>
                            Error Message{sortIndicator("error_message")}
                          </th>
                          <th style={{ minWidth: "150px", cursor: "pointer", userSelect: "none" }} onClick={() => toggleErrorSort("error_at")}>
                            Failed At{sortIndicator("error_at")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => {
                          const k = errKey(row);
                          return (
                            <tr
                              key={i}
                              style={{ cursor: row.retryable ? "pointer" : "default" }}
                              onClick={() => row.retryable && toggleErrorKey(k)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                {row.retryable ? (
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedErrors.has(k)}
                                    onChange={() => toggleErrorKey(k)}
                                  />
                                ) : null}
                              </td>
                              <td
                                className="font-monospace small"
                                style={{ wordBreak: "break-word" }}
                              >
                                {row.filepath}
                              </td>
                              <td className="small">{row.error_type}</td>
                              <td
                                className={`text-center small fw-semibold ${
                                  row.retryable ? "text-success" : "text-muted"
                                }`}
                                title={
                                  row.retryable
                                    ? "A re-run may succeed"
                                    : "A re-run reproduces the same failure"
                                }
                              >
                                {row.retryable ? "Yes" : "No"}
                              </td>
                              <td className="text-danger small" style={{ wordBreak: "break-word" }}>
                                {row.error_message || "—"}
                              </td>
                              <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                                {fmtTime(row.error_at)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CollapsiblePanel>
                );
              })}
          </div>
        </div>
      </div>

      {/* ── Skipped files, grouped by stage ──────────────────────────────── */}
      <div className="col-12">
        <div className="card shadow-sm border-warning border-opacity-25">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              Skipped Files
              {skippedRows !== null && (
                <span className="text-muted fw-normal small ms-2">
                  {skippedTotal} file{skippedTotal !== 1 ? "s" : ""}
                  {skippedTruncated && ` · showing first ${skippedRows.length}`}
                  {Object.entries(skippedCounts).length > 0 &&
                    ` · ${Object.entries(skippedCounts)
                      .map(([reason, n]) => `${n} ${reason.toLowerCase()}`)
                      .join(", ")}`}
                </span>
              )}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {skippedLoading && <span className="text-muted small">Loading…</span>}
              {skippedRows !== null && skippedRows.length > 0 && (
                <button
                  className="btn btn-warning btn-sm"
                  disabled={
                    selectedRetryableSkipCount === 0 || retrying || skippedLoading || !writesAllowed
                  }
                  onClick={handleRetrySkipSelected}
                  title={
                    writesAllowed
                      ? "Reset the selected oversized skips"
                      : "Retry is only permitted on the most recent ingest requests"
                  }
                >
                  {retrying
                    ? "Retrying…"
                    : `Retry Selected${
                        selectedRetryableSkipCount > 0 ? ` (${selectedRetryableSkipCount})` : ""
                      }`}
                </button>
              )}
              {skippedRows !== null && skippedRows.length > 0 && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() =>
                    downloadCsv(
                      skippedRows,
                      [
                        { label: "Stage", get: (r) => layerName(r.layer) },
                        { label: "File", get: (r) => r.filepath },
                        { label: "Skip Reason", get: (r) => r.skip_reason },
                        { label: "Retryable", get: (r) => (r.retryable ? "Yes" : "No") },
                        { label: "Message", get: (r) => r.reason_message || "" },
                        { label: "Skipped At", get: (r) => fmtTime(r.skipped_at) },
                      ],
                      `skipped-files-${orgSlug}${ridSuffix}.csv`
                    )
                  }
                >
                  Export CSV
                </button>
              )}
            </div>
          </div>
          <div className="card-body p-0" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {skipRetryResult && (
              <div
                className={`px-3 pt-2 small ${
                  skipRetryResult.ok ? "text-success" : "text-danger"
                }`}
              >
                {skipRetryResult.message}
              </div>
            )}
            {!selectedOrgId && (
              <div className="p-3 text-muted small fst-italic">
                Select an organization to view skipped files.
              </div>
            )}
            {selectedOrgId && (skippedRows === null || skippedLoading) && !skippedError && (
              <div className="p-3 text-muted small">Loading…</div>
            )}
            {skippedError && <div className="p-3 text-danger small">{skippedError}</div>}
            {skippedRows !== null &&
              !skippedLoading &&
              skippedRows.length === 0 &&
              !skippedError && (
                <div className="p-3 text-success small">No files skipped in this request.</div>
              )}
            {skippedTruncated && (
              <div className="px-3 pt-2 text-warning small">
                Too many skipped files to list them all. The counts cover every file; the
                table shows the first {skippedRows.length}. “Retry All” still acts on every one.
              </div>
            )}
            {skippedRows !== null &&
              skippedRows.length > 0 &&
              groupByLayer(skippedRows).map(([layer, rows]) => {
                const key = `skip::${layer}`;
                const oversizedRows = rows.filter((r) => r.retryable);
                const oversized = oversizedRows.length;
                const selectedHere = oversizedRows.filter((r) =>
                  selectedSkips.has(errKey(r))
                ).length;
                const allSelected = oversized > 0 && selectedHere === oversized;
                return (
                  <CollapsiblePanel
                    key={key}
                    panelKey={key}
                    openKeys={openSkipPanels}
                    setOpenKeys={setOpenSkipPanels}
                    header={layerName(layer)}
                    badges={
                      <>
                        <span className="badge bg-warning-subtle text-warning-emphasis">
                          {rows.length}
                        </span>
                        {oversized > 0 && (
                          <span className="badge bg-secondary-subtle text-secondary-emphasis">
                            {oversized} retryable
                          </span>
                        )}
                        {selectedHere > 0 && (
                          <span className="badge bg-warning-subtle text-warning-emphasis">
                            {selectedHere} selected
                          </span>
                        )}
                      </>
                    }
                    actions={
                      oversized > 0 && (
                        <>
                          <button
                            className="btn btn-outline-secondary btn-sm py-0"
                            onClick={() =>
                              setSelectedSkips((prev) => {
                                const next = new Set(prev);
                                oversizedRows.forEach((r) =>
                                  allSelected ? next.delete(errKey(r)) : next.add(errKey(r))
                                );
                                return next;
                              })
                            }
                          >
                            {allSelected ? "Deselect all" : "Select oversized"}
                          </button>
                          <button
                            className="btn btn-warning btn-sm py-0"
                            disabled={retrying || !writesAllowed}
                            onClick={() => handleRetryAllInLayer("skip", layer)}
                            title={
                              writesAllowed
                                ? "Reset every oversized skip in this stage"
                                : "Retry is only permitted on the most recent ingest requests"
                            }
                          >
                            Retry All ({oversized})
                          </button>
                        </>
                      )
                    }
                  >
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "32px" }} />
                          <th style={{ minWidth: "220px" }}>File</th>
                          <th style={{ minWidth: "150px" }}>Skip Reason</th>
                          <th style={{ minWidth: "90px" }} className="text-center">
                            Retryable
                          </th>
                          <th style={{ minWidth: "280px" }}>Message</th>
                          <th style={{ minWidth: "150px" }}>Skipped At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => {
                          const k = errKey(row);
                          return (
                          <tr
                            key={i}
                            style={{ cursor: row.retryable ? "pointer" : "default" }}
                            onClick={() => row.retryable && toggleSkipKey(k)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              {row.retryable ? (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedSkips.has(k)}
                                  onChange={() => toggleSkipKey(k)}
                                />
                              ) : null}
                            </td>
                            <td className="font-monospace small" style={{ wordBreak: "break-word" }}>
                              {row.filepath}
                            </td>
                            <td className="small">{row.skip_reason}</td>
                            <td
                              className={`text-center small fw-semibold ${
                                row.retryable ? "text-success" : "text-muted"
                              }`}
                              title={
                                row.retryable
                                  ? "Oversized — a re-run can succeed once the budget or source file changes"
                                  : "No extractor supports this file type; a re-run reproduces the same skip"
                              }
                            >
                              {row.retryable ? "Yes" : "No"}
                            </td>
                            <td className="text-muted small" style={{ wordBreak: "break-word" }}>
                              {row.reason_message || "—"}
                            </td>
                            <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                              {fmtTime(row.skipped_at)}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CollapsiblePanel>
                );
              })}
          </div>
        </div>
      </div>

      {/* ── Blob connector utility (collapsed) ───────────────────────────── */}
      {selectedOrgId && connectors !== null && !hasBlobConnector && (
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <span className="small fw-semibold">Add blob connector</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowConnectorForm((v) => !v)}
              >
                {showConnectorForm ? "Hide" : "Show"}
              </button>
            </div>
            {showConnectorForm && (
              <div className="card-body py-2">
                <div className="text-muted small mb-2">
                  No azure-blob-storage connector is configured for {selectedOrgName}.
                </div>
                <div className="mb-2" style={{ maxWidth: "40rem" }}>
                  <label className="form-label small fw-semibold mb-1">Connection String</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                    value={connectionString}
                    onChange={(e) => setConnectionString(e.target.value)}
                    disabled={creating}
                  />
                </div>
                {createResult && (
                  <div
                    className={`alert py-1 small mb-2 ${
                      createResult.ok ? "alert-success" : "alert-danger"
                    }`}
                  >
                    {createResult.message}
                  </div>
                )}
                <button
                  className="btn btn-sm btn-primary"
                  disabled={!connectionString.trim() || creating}
                  onClick={handleCreateConnector}
                >
                  {creating ? "Creating…" : "Create Blob Connector"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
